import { useCallback, useState } from "react"
import { useForm } from "react-hook-form"
import { Eye, EyeOff } from "lucide-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { toast } from "sonner"

import { authenticate } from "@/actions/auth"
import { loginSchema, type LoginSchema } from "@/schemas/loginSchema"
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { useToken } from "@/hooks/useToken"
import { Spinner } from "@/components/ui/spinner"

const LoginForm = () => {
    const [token] = useToken()
    const [loading, setLoading] = useState(false)
    const [passwordVisible, setPasswordVisible] = useState(false)

    const login = useForm<LoginSchema>({
        resolver: zodResolver(loginSchema),
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        defaultValues: {
            username: "",
            password: "",
        },
    })

    const loginValidity = useCallback(async (values: LoginSchema) => {
        setLoading(true)
        const { data, error } = await authenticate({ ...values, id: token })
        if (error) {
            setLoading(false)
            toast.error("El usuario y la contraseña no son válidos. Por favor revíselos e inténtelo nuevamente")
            return
        }
        location.replace(data!.redirectTo)
    }, [token])

    const togglePassword = useCallback(() => setPasswordVisible(current => !current), [])

    return (
        <Form {...login}>
            <form onSubmit={login.handleSubmit(loginValidity)} className="space-y-8">
                <FormField
                    control={login.control}
                    name="username"
                    render={({ field }) => (
                        <FormItem className="mb-6">
                            <FormControl>
                                <Input className="h-12 bg-gray-50" autoComplete="username" placeholder="Usuario" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={login.control}
                    name="password"
                    render={({ field }) => (
                        <FormItem className="mb-6">
                            <FormControl>
                                <div className="relative">
                                    <Input
                                        className="h-12 bg-gray-50"
                                        type={passwordVisible ? "text" : "password"}
                                        autoComplete="current-password"
                                        placeholder="Contraseña"
                                        {...field}
                                    />
                                    <Button type="button" size="icon" className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2" variant="ghost" onClick={togglePassword}>
                                        {passwordVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <Button data-loading={loading} disabled={loading} className="h-10 w-full cursor-pointer bg-red-500 data-[loading='true']:pointer-events-none data-[loading='true']:bg-red-500/85 hover:bg-red-600" type="submit">
                    {loading && <Spinner />}
                    {loading ? "Cargando..." : "Ingresar"}
                </Button>
            </form>
        </Form>
    )
}

export default LoginForm
